import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import {
  EnumAssetAdvancedProcessing,
  EnumAssetVectorEmbeddings,
} from "../../__generated__/graphql";
import Asset from "../../models/Asset";
import FileExtension from "../FileExtension";
import { Spinner } from "../LoadingIndicators/Spinner";

type AttachmentProgressProps = {
  assets: Asset[];
  onRetry?: (assetId: string) => Promise<void>;
};

function AttachmentProgress({ assets, onRetry }: AttachmentProgressProps) {
  const [retryingAssets, setRetryingAssets] = useState<Set<string>>(new Set());

  const getProcessingMessage = (asset: Asset) => {
    // Check advanced processing first (visual PDF processing)
    if (asset.advancedProcessing === EnumAssetAdvancedProcessing.InProgress) {
      return "Processing visual content (images, charts, diagrams)...";
    }
    if (asset.advancedProcessing === EnumAssetAdvancedProcessing.Error) {
      return "Visual processing failed";
    }
    // Then check basic vector embeddings
    if (asset.vectorEmbeddings === EnumAssetVectorEmbeddings.InProgress) {
      return "Analyzing your file contents...";
    }
    if (asset.vectorEmbeddings === EnumAssetVectorEmbeddings.Error) {
      return "Processing failed";
    }
    if (
      asset.vectorEmbeddings === EnumAssetVectorEmbeddings.Successful &&
      (asset.advancedProcessing === EnumAssetAdvancedProcessing.Successful ||
        !asset.advancedProcessing)
    ) {
      return "Processing complete";
    }
    return null;
  };

  const isProcessing = (asset: Asset) =>
    asset.vectorEmbeddings === EnumAssetVectorEmbeddings.InProgress ||
    asset.advancedProcessing === EnumAssetAdvancedProcessing.InProgress;

  const hasError = (asset: Asset) =>
    asset.vectorEmbeddings === EnumAssetVectorEmbeddings.Error ||
    asset.advancedProcessing === EnumAssetAdvancedProcessing.Error;

  const isComplete = (asset: Asset) =>
    asset.vectorEmbeddings === EnumAssetVectorEmbeddings.Successful &&
    (asset.advancedProcessing === EnumAssetAdvancedProcessing.Successful ||
      !asset.advancedProcessing);

  const handleRetry = async (assetId: string) => {
    console.log("Clicked on Retry");
    try {
      setRetryingAssets((prev) => new Set(prev).add(assetId));
      await onRetry?.(assetId);
    } finally {
      setRetryingAssets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(assetId);
        return newSet;
      });
    }
  };

  return (
    <div className="w-full rounded-lg overflow-hidden border border-gray-400 border-solid">
      <div className="flex items-center justify-between p-6 bg-gradient-to-r from-blue-500 to-purple-600">
        <h2 className="text-2xl font-bold text-white flex items-center">
          Processing Files
        </h2>
      </div>

      <div className="p-6">
        <div className="w-full space-y-4">
          <AnimatePresence mode="wait">
            {assets.map((asset) => {
              if (isComplete(asset)) return null;

              const message = getProcessingMessage(asset);
              return (
                <motion.div
                  key={asset.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="p-4 bg-white border border-gray-400 rounded-lg shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex gap-4 items-center">
                      <FileExtension title={asset.name} />

                      <span className="text-lg text-gray-600 font-medium">
                        {asset.name}
                      </span>
                    </div>

                    {isProcessing(asset) && (
                      <div className="flex items-center text-gray-600">
                        <Spinner size={20} color="gray" />
                        <span className="ml-2 text-md">{message}</span>
                      </div>
                    )}

                    {hasError(asset) && (
                      <div className="flex items-center">
                        <span className="text-sm text-red-500 mr-2">
                          {message}
                        </span>
                        <button
                          onClick={() => handleRetry(asset.id)}
                          disabled={retryingAssets.has(asset.id)}
                          className={`px-4 py-2 text-white rounded-full transition-colors flex items-center ${
                            retryingAssets.has(asset.id)
                              ? "bg-blue-300 cursor-not-allowed"
                              : "bg-blue-500 hover:bg-blue-600"
                          }`}
                        >
                          <ArrowPathIcon
                            className={`w-4 h-4 mr-2 ${
                              retryingAssets.has(asset.id) ? "animate-spin" : ""
                            }`}
                          />
                          {retryingAssets.has(asset.id)
                            ? "Retrying..."
                            : "Retry"}
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default AttachmentProgress;
